/**
 * @prop --icon-size: Icon size
 */

:host {
  @include spaceCurve('--content-padding-bottom', 'regular');
  --trigger-size: 18px;
  --icon-size: #{iconSize()};
  --description-color: #{$colorBlack};
  --heading-margin-bottom: #{space('medium')};
  --button-color: #{$colorPrimary70};
  --button-background-color: transparent;
  --button-font-size: var(--stzh-timeline-nested-button-font-size, var(--stzh-font-milli-font-size));
  --button-line-height: var(--stzh-timeline-nested-button-line-height, var(--stzh-font-milli-heading-line-height));
  --button-letter-spacing: var(--stzh-timeline-nested-button-line-height, normal);
  --bullet-color: #{$colorPrimary70};
  --bullet-background-color: #{$colorGrey20};
  --content-padding-top: var(--stzh-timeline-content-padding-top);
  --content-padding-right: 0px;

  --hover-button-color: var(--button-color);
  --hover-button-background-color: var(--button-background-color);
  --hover-bullet-color: var(--bullet-color);
  --hover-bullet-background-color: var(--bullet-background-color);

  @media print {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  @include mq($from: small) {
    --button-font-size: var(--stzh-timeline-nested-button-font-size, var(--stzh-font-centi-font-size));
    --button-line-height: var(--stzh-timeline-nested-button-line-height, var(--stzh-font-centi-heading-line-height));
  }
}

/* Foldable */

:host(:where([is-foldable])) {
  --button-background-color: #{$colorGrey10};
  --bullet-color: #{$colorBlack};
  --bullet-background-color: #{$colorSecondary20};

  --hover-button-background-color: #{$colorGrey20};
  --hover-bullet-color: var(--bullet-color);
  --hover-bullet-background-color: #{$colorSecondary40};

  @include highContrast() {
    --button-color: Highlight;

    --hover-button-color: Highlight;
  }
}

/* Open */

:host(:where([open]:not([open="false"]))) {
  --heading-margin-bottom: 0px;
}

/* Foldable Open */

:host(:where([is-foldable][open]:not([open="false"]))) {
  --bullet-color: #{$colorWhite};
  --bullet-background-color: #{$colorPrimary70};

  --hover-bullet-color: var(--bullet-color);
  --hover-bullet-background-color: #{$colorPrimary90};

  @include highContrast() {
    --button-color: SelectedItem;
    --bullet-color: SelectedItem;

    --hover-button-color: SelectedItem;
    --hover-bullet-color: SelectedItem;
  }
}

/* Completed */

:host(:where([completed]:not([completed="false"]))) {
  --bullet-color: #{$colorWhite};
  --bullet-background-color: #{$colorSecondary60};

  --hover-bullet-color: var(--bullet-color);
  --hover-bullet-background-color: var(--bullet-background-color);
}

/* Completed Foldable */

:host(:where([is-foldable][open]:not([open="false"]))) {
  --hover-bullet-background-color: #{$colorSecondary70};
}

/* Disabled */

:host(:where([disabled]:not([disabled="false"]))) {
  --button-color: #{$colorGrey80};
  --bullet-color: #{$colorWhite};
  --bullet-background-color: #{$colorGrey60};

  --hover-button-color: var(--button-color);
  --hover-button-background-color: var(--button-background-color);
  --hover-bullet-color: var(--bullet-color);
  --hover-bullet-background-color: var(--bullet-background-color);
}

.stzh-timeline-item {
  display: flex;
  flex-direction: column;

  &__heading {
    position: relative;
    margin: 0;
  }

  &__button {
    font-size: var(--button-font-size);
    line-height: var(--button-line-height);
    letter-spacing: var(--button-letter-spacing);
    font-family: inherit;
    width: 100%;
    appearance: none;
    background-color: transparent;
    padding: 0;
    border: none;
    display: flex;
    color: var(--button-color);
    text-align: left;
    transition-property: color;
    transition-duration: $baseTransitionAnimationSpeed;
    cursor: default;

    &:hover {
      color: var(--hover-button-color);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  &__button-inner {
    display: flex;
    flex-grow: 1;
    align-items: center;
    padding-top: space('xsmall');
    padding-bottom: space('xsmall');
    background-color: var(--button-background-color);
    transition-property: background-color;
    transition-duration: $baseTransitionAnimationSpeed;
    min-height: $timelineBulletSize;
    padding-left: space('xsmall');
    padding-right: space('small');
  }

  &__button:hover &__button-inner {
    background-color: var(--hover-button-background-color);
  }

  &__button,
  &__content-inner {
    padding-left: $timelineBulletSize;
  }

  &__button-text {
    @include font('heavy');
    flex-grow: 1;
  }

  &__button-icon {
    @include spaceCurve('margin-left', 'small');
    align-self: flex-start;
    position: relative;
    display: block;
    width: var(--trigger-size);
    height: var(--trigger-size);
    margin-top: space('xxsmall');

    &::before,
    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      content: '';
      display: block;
      width: var(--trigger-size);
      height: 2.5px;
      transition-property: transform;
      transition-duration: 500ms;
      transform-origin: top left;
      background-color: currentColor;

      @include highContrast() {
        background-color: CanvasText;
      }

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    }

    &::before {
      transform: rotate(180deg) translate(-50%, -50%);
    }

    &::after {
      transform: rotate(90deg) translate(-50%, -50%);
    }
  }

  &__bullet {
    @include font('heavy');
    @include fontSize('milli');
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    width: $timelineBulletSize;
    height: $timelineBulletSize;
    color: var(--bullet-color);
    background-color: var(--bullet-background-color);
    transition-property: color, background-color;
    transition-duration: $baseTransitionAnimationSpeed;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  &__button:hover &__bullet {
    color: var(--hover-bullet-color);
    background-color: var(--hover-bullet-background-color);
  }

  &__bullet-icon-wrapper {
    --stzh-icon-size: var(--icon-size);

    display: flex;
  }

  &__content {
    // content padding of nested timelines
    @include spaceCurve('--stzh-timeline-content-padding-top', 'tiny');
    --stzh-timeline-nested-button-font-size: var(--stzh-font-micro-font-size);
    --stzh-timeline-nested-button-line-height: var(--stzh-font-micro-heading-line-height);

    overflow: hidden;

    @include mq($from: medium) {
      --stzh-timeline-nested-button-font-size: var(--stzh-font-milli-font-size);
      --stzh-timeline-nested-button-line-height: var(--stzh-font-milli-heading-line-height);
    }

    @media print {
      height: auto !important;
      overflow: visible !important;
      display: block !important;
    }
  }

  &__content-inner {
    padding-top: var(--content-padding-top);
    padding-bottom: var(--content-padding-bottom);
    padding-right: var(--content-padding-right);
  }

  &__badge {
    position: absolute;
    right: -5px;
    top: -6px;
  }

  &__description {
    @include fontCurve('caption');
    color: var(--description-color);
    transition: color $baseTransitionAnimationSpeed;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  &__heading {
    margin-bottom: var(--heading-margin-bottom);
    transition: margin-bottom $baseTransitionAnimationSpeed;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  &__heading-text,
  &__description {
    display: block;
  }

  /* Foldable */

  &--is-foldable &__button {
    cursor: pointer;
  }

  /* Open */

  &--is-open &__button-icon {
    &::before,
    &::after {
      transform: rotate(0) translate(-50%, -50%);
    }
  }

  /* Disabled */

  &--is-disabled &__button {
    cursor: not-allowed;
  }
}
